export type ApiErrorEnvelope = {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown> | null;
    request_id?: string;
  };
};

let bearerToken = "";
let platformBearerToken = "";

export function setApiToken(token: string) {
  bearerToken = token;
}

export function setPlatformApiToken(token: string) {
  platformBearerToken = token;
}

type ApiOptions = {
  auth?: "auto" | "tenant" | "platform" | "none";
  headers?: Record<string, string>;
};

function tokenForPath(path: string, options?: ApiOptions): string {
  const auth = options?.auth ?? "auto";
  if (auth === "none") return "";
  if (auth === "tenant") return bearerToken;
  if (auth === "platform") return platformBearerToken;
  if (path.startsWith("/v3/sys/")) return platformBearerToken;
  return bearerToken;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = body as ApiErrorEnvelope;
    const message = err.error?.message ?? `Request failed (${res.status})`;
    const code = err.error?.code ?? "unknown_error";
    const req = err.error?.request_id ? ` request_id=${err.error.request_id}` : "";
    throw new Error(`${code}: ${message}${req}`);
  }
  return body as T;
}

function buildHeaders(path: string, options?: ApiOptions, initial: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { ...initial, ...(options?.headers ?? {}) };
  const token = tokenForPath(path, options);
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export async function apiGet<T>(path: string, options?: ApiOptions): Promise<T> {
  const headers = buildHeaders(path, options);
  const res = await fetch(path, { headers });
  return parseResponse<T>(res);
}

export async function apiPost<T>(path: string, payload: unknown, options?: ApiOptions): Promise<T> {
  const headers = buildHeaders(path, options, { "Content-Type": "application/json" });
  const res = await fetch(path, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  return parseResponse<T>(res);
}

export async function apiPut<T>(path: string, payload: unknown, options?: ApiOptions): Promise<T> {
  const headers = buildHeaders(path, options, { "Content-Type": "application/json" });
  const res = await fetch(path, {
    method: "PUT",
    headers,
    body: JSON.stringify(payload),
  });
  return parseResponse<T>(res);
}

export async function apiPatch<T>(path: string, payload: unknown, options?: ApiOptions): Promise<T> {
  const headers = buildHeaders(path, options, { "Content-Type": "application/json" });
  const res = await fetch(path, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  return parseResponse<T>(res);
}

export async function apiDelete<T>(path: string, options?: ApiOptions): Promise<T> {
  const headers = buildHeaders(path, options);
  const res = await fetch(path, { method: "DELETE", headers });
  return parseResponse<T>(res);
}
